import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DollarSign, Plus, Edit, Trash2, X, Search,
  Footprints, Shirt, Stethoscope, Brain, GraduationCap,
  Plane, ShoppingBasket, Wallet, MoreHorizontal, Users, TrendingUp, Calendar,
  Boxes, Package, AlertTriangle, ChevronsUpDown, Check, CalendarClock,
} from 'lucide-react';

import { isRecurringType, currentMonth } from '@/lib/payments';

const API_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

// Investment types catalog (single source of truth)
// IMPORTANT: literal Tailwind classes (bg-X-50, text-X-700, border-X-200) so Tailwind doesn't purge them
const TYPE_STYLES = {
  championes:        { bg: 'bg-orange-50',   text: 'text-orange-700',   border: 'border-orange-200' },
  indumentaria:      { bg: 'bg-purple-50',   text: 'text-purple-700',   border: 'border-purple-200' },
  estudios_medicos:  { bg: 'bg-red-50',      text: 'text-red-700',      border: 'border-red-200' },
  psicologo:         { bg: 'bg-pink-50',     text: 'text-pink-700',     border: 'border-pink-200' },
  educacion:         { bg: 'bg-indigo-50',   text: 'text-indigo-700',   border: 'border-indigo-200' },
  viaticos:          { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-200' },
  canasta_alimentos: { bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-200' },
  adelanto:          { bg: 'bg-green-50',    text: 'text-green-700',    border: 'border-green-200' },
  otros:             { bg: 'bg-gray-50',     text: 'text-gray-700',     border: 'border-gray-200' },
};

export const INVESTMENT_TYPES = [
  { value: 'championes', label: 'Championes', icon: Footprints },
  { value: 'indumentaria', label: 'Vestimenta / Indumentaria', icon: Shirt },
  { value: 'estudios_medicos', label: 'Estudios médicos', icon: Stethoscope },
  { value: 'psicologo', label: 'Psicólogo', icon: Brain },
  { value: 'educacion', label: 'Profesora particular / Educación', icon: GraduationCap },
  { value: 'viaticos', label: 'Viáticos', icon: Plane },
  { value: 'canasta_alimentos', label: 'Canasta de alimentos', icon: ShoppingBasket },
  { value: 'adelanto', label: 'Adelanto en efectivo', icon: Wallet },
  { value: 'otros', label: 'Otros', icon: MoreHorizontal },
];

const TYPE_BY_VALUE = INVESTMENT_TYPES.reduce((acc, t) => {
  acc[t.value] = t;
  return acc;
}, {});

const fmt = (n) => new Intl.NumberFormat('es-UY', {
  style: 'currency', currency: 'UYU', maximumFractionDigits: 0,
}).format(n || 0);

const fmtDate = (s) => {
  if (!s) return '-';
  try {
    const d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
};

// Reusable player picker with type-ahead search
function PlayerCombobox({ value, onChange, players, placeholder = 'Buscar jugador...', allowAll = false, allLabel = 'Todos los jugadores', dataTestId }) {
  const [open, setOpen] = useState(false);
  const selected = players.find((p) => p.id === value);
  const label = value === 'all' && allowAll
    ? allLabel
    : selected ? `${selected.first_name} ${selected.last_name}` : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal h-9"
          data-testid={dataTestId}
        >
          <span className={!selected && value !== 'all' ? 'text-gray-400' : ''}>{label}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput placeholder="Escribí para buscar..." />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {allowAll && (
                <CommandItem
                  value="__all__"
                  onSelect={() => { onChange('all'); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                  {allLabel}
                </CommandItem>
              )}
              {players.map((p) => {
                const fn = `${p.first_name} ${p.last_name}`;
                return (
                  <CommandItem
                    key={p.id}
                    value={fn}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === p.id ? 'opacity-100' : 'opacity-0'}`} />
                    {fn}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Investments() {
  const { token, isSuperAdmin, isAdmin } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterPlayer, setFilterPlayer] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'inventory' ? 'inventory' : 'movements';
  const [activeTab, setActiveTab] = useState(initialTab);
  // Low-stock filter from querystring (?filter=low_stock)
  const [lowStockOnly, setLowStockOnly] = useState(searchParams.get('filter') === 'low_stock');
  
  // Inventory state (lazy-loaded when tab clicked)
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [showInvItemModal, setShowInvItemModal] = useState(false);
  const [editingInvItem, setEditingInvItem] = useState(null);

  const fetchInvestments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (filterPlayer !== 'all') params.append('player_id', filterPlayer);
      if (filterType !== 'all') params.append('type', filterType);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      if (search) params.append('search', search);

      const res = await fetch(`${API_URL}/api/investments?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setInvestments(data.data || []);
      setTotalItems(data.pagination?.total || 0);
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (e) {
      toast.error('Error al cargar inversiones');
    }
  }, [token, currentPage, itemsPerPage, filterPlayer, filterType, filterDateFrom, filterDateTo, search]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const res = await fetch(`${API_URL}/api/investments/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(await res.json());
    } catch (e) { /* noop */ }
  }, [token, filterDateFrom, filterDateTo]);

  const fetchPlayers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/players?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPlayers(data.data || []);
    } catch (e) { /* noop */ }
  }, [token]);

  const fetchInventory = useCallback(async () => {
    try {
      const [itemsRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/materials/items?limit=500`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/materials/categories`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const itemsData = await itemsRes.json();
      const catsData = await catsRes.json();
      // Only show categories relevant for inversiones (championes / indumentaria) — filter UI-side
      const relevantCats = catsData.filter(c => 
        ['championes', 'indumentaria'].some(k => c.name.toLowerCase().includes(k))
      );
      const relevantCatIds = new Set(relevantCats.map(c => c.id));
      const filteredItems = (itemsData.data || []).filter(i => relevantCatIds.has(i.material_category_id));
      setInventoryItems(filteredItems);
      setInventoryCategories(relevantCats);
      setInventoryLoaded(true);
    } catch (e) {
      toast.error('Error al cargar inventario');
    }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchInvestments(), fetchSummary(), fetchPlayers()]).finally(() => setLoading(false));
  }, [fetchInvestments, fetchSummary, fetchPlayers]);

  // Lazy load inventory only when first switching to that tab
  useEffect(() => {
    if (activeTab === 'inventory' && !inventoryLoaded) {
      fetchInventory();
    }
  }, [activeTab, inventoryLoaded, fetchInventory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterPlayer, filterType, filterDateFrom, filterDateTo, search]);

  const handleSave = async (data) => {
    try {
      if (data.recurring) {
        const { recurring, ...plan } = data;
        const resPlan = await fetch(`${API_URL}/api/recurring/plans`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(plan),
        });
        if (!resPlan.ok) {
          const err = await resPlan.json().catch(() => ({}));
          throw new Error(typeof err.detail === 'string' ? err.detail : 'Error al configurar el pago mensual');
        }
        toast.success('Pago mensual configurado. Aparece en "Pagos mensuales".');
        setShowModal(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/players/${data.player_id}/investments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al guardar');
      }
      toast.success('Inversión registrada');
      setShowModal(false);
      fetchInvestments();
      fetchSummary();
      // Refetch inventory if user took from stock (so the badge updates)
      if (data.material_item_id) {
        fetchInventory();
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`¿Eliminar inversión de ${fmt(inv.amount)} de ${inv.player_name}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/players/${inv.player_id}/investments/${inv.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'No se pudo eliminar');
      }
      toast.success('Inversión eliminada');
      fetchInvestments();
      fetchSummary();
      // Refetch inventory if it was linked to a material (stock got restored)
      if (inv.material_item_id) {
        fetchInventory();
      }
    } catch (e) {
      toast.error(e.message || 'No se pudo eliminar');
    }
  };

  const clearFilters = () => {
    setFilterPlayer('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearch('');
  };

  // Inventory CRUD
  const handleSaveInvItem = async (data) => {
    try {
      const url = editingInvItem && editingInvItem.id
        ? `${API_URL}/api/materials/items/${editingInvItem.id}`
        : `${API_URL}/api/materials/items`;
      const method = editingInvItem && editingInvItem.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al guardar');
      }
      const saved = await res.json().catch(() => null);
      toast.success((editingInvItem && editingInvItem.id) ? 'Item actualizado' : 'Item agregado al inventario');
      setShowInvItemModal(false);
      setEditingInvItem(null);
      await fetchInventory();
      // Return saved id so the caller (modal) can preselect it
      return saved?.id || null;
    } catch (e) {
      toast.error(e.message);
      return null;
    }
  };

  const handleDeleteInvItem = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.name}" del inventario?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/materials/items/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'No se puede eliminar');
      }
      toast.success('Item eliminado');
      fetchInventory();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hasFilters = filterPlayer !== 'all' || filterType !== 'all' || filterDateFrom || filterDateTo || search;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="investments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Inversiones en Jugadores</h1>
            <p className="text-gray-500 mt-1">
              Registro de todo lo que la agencia entrega o paga a cada representado
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(isSuperAdmin || isAdmin) && (
              <Link to="/pagos-mensuales">
                <Button variant="outline" data-testid="go-monthly-payments">
                  <CalendarClock className="h-4 w-4 mr-2" />
                  Pagos mensuales
                </Button>
              </Link>
            )}
            <Button
              onClick={() => setShowModal(true)}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="register-investment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Registrar inversión
            </Button>
          </div>
        </div>

        {/* Summary stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="stat-total-invested">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-brand-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl md:text-2xl font-bold text-gray-900 truncate">
                      {fmt(summary.total_invested)}
                    </p>
                    <p className="text-xs text-gray-500">Total invertido</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{summary.players_count}</p>
                    <p className="text-xs text-gray-500">Jugadores beneficiados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{summary.investments_count}</p>
                    <p className="text-xs text-gray-500">Movimientos registrados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {summary.by_type?.[0]
                        ? TYPE_BY_VALUE[summary.by_type[0].type]?.label.split(' / ')[0] || summary.by_type[0].type
                        : '—'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Tipo más invertido {summary.by_type?.[0] ? `(${fmt(summary.by_type[0].amount)})` : ''}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs: Movimientos | Inventario */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger value="movements" className="data-[state=active]:bg-white" data-testid="tab-movements">
              <DollarSign className="h-4 w-4 mr-2" />
              Movimientos
            </TabsTrigger>
            <TabsTrigger value="inventory" className="data-[state=active]:bg-white" data-testid="tab-inventory">
              <Boxes className="h-4 w-4 mr-2" />
              Inventario <span className="text-xs text-gray-500 ml-1">(opcional)</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="movements" className="space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-9 pl-8"
                    placeholder="Descripción o jugador..."
                  />
                </div>
              </div>
              <div className="w-56">
                <Label className="text-xs text-gray-500">Jugador</Label>
                <PlayerCombobox
                  value={filterPlayer}
                  onChange={setFilterPlayer}
                  players={players}
                  allowAll
                  allLabel="Todos los jugadores"
                  dataTestId="filter-player-combobox"
                />
              </div>
              <div className="w-48">
                <Label className="text-xs text-gray-500">Tipo</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {INVESTMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs text-gray-500">Desde</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="w-36">
                <Label className="text-xs text-gray-500">Hasta</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="h-9"
                />
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Investments list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              Inversiones registradas
              <span className="text-sm font-normal text-gray-500 ml-2">({totalItems})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalItems === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">
                  {hasFilters ? 'Sin resultados' : 'No hay inversiones registradas todavía'}
                </p>
                <p className="text-sm mt-1">
                  {hasFilters
                    ? 'Probá quitar algún filtro'
                    : 'Registrá la primera inversión para empezar a ver el detalle por jugador'}
                </p>
                {!hasFilters && (
                  <Button
                    className="mt-4 bg-brand-600 hover:bg-brand-700"
                    onClick={() => setShowModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar inversión
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-xs text-gray-500 uppercase">
                      <th className="text-left py-2 pr-3">Fecha</th>
                      <th className="text-left py-2 pr-3">Jugador</th>
                      <th className="text-left py-2 pr-3">Tipo</th>
                      <th className="text-left py-2 pr-3">Descripción</th>
                      <th className="text-right py-2 pr-3">Monto</th>
                      <th className="text-right py-2 pr-3 w-24">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {investments.map((inv) => {
                      const typeMeta = TYPE_BY_VALUE[inv.type] || TYPE_BY_VALUE.otros;
                      const Icon = typeMeta.icon;
                      const styles = TYPE_STYLES[inv.type] || TYPE_STYLES.otros;
                      return (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{fmtDate(inv.date)}</td>
                          <td className="py-2 pr-3">
                            <Link
                              to={`/players/${inv.player_id}?tab=investment`}
                              className="font-medium text-gray-900 hover:text-brand-600 hover:underline"
                            >
                              {inv.player_name}
                            </Link>
                          </td>
                          <td className="py-2 pr-3">
                            <Badge
                              variant="outline"
                              className={`text-xs ${styles.bg} ${styles.text} ${styles.border} inline-flex items-center gap-1`}
                            >
                              <Icon className="h-3 w-3" />
                              {typeMeta.label.split(' / ')[0]}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-gray-700">
                            <div className="max-w-xs truncate" title={inv.description}>
                              {inv.description || <span className="text-gray-400">—</span>}
                            </div>
                            {inv.notes && (
                              <div className="text-xs text-gray-400 truncate max-w-xs" title={inv.notes}>
                                {inv.notes}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold text-red-600 tabular-nums">
                            {fmt(inv.amount)}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(inv)}
                              data-testid={`delete-inv-${inv.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
              itemName="inversiones"
            />
          </CardContent>
        </Card>
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Inventario de Championes / Indumentaria</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Cargá items que la agencia compra en cantidad (opcional). Cuando registres una inversión de Championes o Indumentaria podés tomarlo de acá y descuenta stock automáticamente.
                    </p>
                  </div>
                  <Button
                    onClick={() => { setEditingInvItem(null); setShowInvItemModal(true); }}
                    className="bg-brand-600 hover:bg-brand-700"
                    data-testid="add-inventory-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar al inventario
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!inventoryLoaded ? (
                  <div className="text-center py-8 text-gray-400">Cargando...</div>
                ) : inventoryItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Boxes className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No hay items en el inventario</p>
                    <p className="text-sm mt-1">
                      Es opcional. Si comprás championes en cantidad podés cargarlos acá; si pagás caso por caso, ignorá esta sección.
                    </p>
                  </div>
                ) : (
                  <>
                  {lowStockOnly && (
                    <div className="mb-3 flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                      <p className="text-sm text-orange-800">
                        <AlertTriangle className="inline h-4 w-4 mr-1" />
                        Mostrando solo items con <strong>stock bajo</strong> ({inventoryItems.filter(i => (i.quantity||0) <= (i.min_stock||0)).length})
                      </p>
                      <Button size="sm" variant="ghost" className="text-orange-700 hover:bg-orange-100" onClick={() => setLowStockOnly(false)} data-testid="clear-low-stock-filter">
                        Ver todos
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {inventoryItems
                      .filter(it => !lowStockOnly || ((it.quantity || 0) <= (it.min_stock || 0)))
                      .map((it) => {
                      const cat = inventoryCategories.find(c => c.id === it.material_category_id);
                      const isChampiones = (cat?.name || '').toLowerCase().includes('champion');
                      const Icon = isChampiones ? Footprints : Shirt;
                      const isLow = (it.quantity || 0) <= (it.min_stock || 0);
                      return (
                        <Card key={it.id} className={`hover:shadow-md transition-shadow ${isLow ? 'border-orange-200' : ''}`}>
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-lg bg-gray-100 border flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {it.image_url ? (
                                  <img
                                    src={it.image_url.startsWith('/api') ? `${API_URL}${it.image_url}` : it.image_url}
                                    alt={it.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <Icon className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">{it.name}</h3>
                                <p className="text-xs text-gray-500">{cat?.name || '—'}</p>
                                {(it.provider || it.size) && (
                                  <p className="text-xs text-gray-600 mt-0.5 truncate">
                                    {it.provider && <span>{it.provider}</span>}
                                    {it.provider && it.size && <span className="mx-1 text-gray-300">·</span>}
                                    {it.size && <span>Talle {it.size}</span>}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <Badge variant="outline" className={isLow ? 'bg-orange-50 text-orange-700 border-orange-200' : ''}>
                                    Stock: {it.quantity || 0}
                                  </Badge>
                                  {it.unit_price > 0 && (
                                    <Badge variant="outline">{fmt(it.unit_price)}</Badge>
                                  )}
                                  {isLow && <AlertTriangle className="h-4 w-4 text-orange-500" />}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => { setEditingInvItem(it); setShowInvItemModal(true); }}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteInvItem(it)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {showModal && (
          <InvestmentModal
            players={players}
            inventoryItems={inventoryItems}
            inventoryCategories={inventoryCategories}
            inventoryLoaded={inventoryLoaded}
            onLoadInventory={fetchInventory}
            onSave={handleSave}
            onClose={() => setShowModal(false)}
            onCreateItem={(typeKey) => {
              // Determine target category by type
              const catName = typeKey === 'championes' ? 'champion' : 'indument';
              const cat = inventoryCategories.find(c => c.name.toLowerCase().includes(catName));
              setEditingInvItem({ _prefilledCategoryId: cat?.id || '' });
              setShowInvItemModal(true);
            }}
          />
        )}
        
        {showInvItemModal && (
          <InventoryItemModal
            item={editingInvItem}
            categories={inventoryCategories}
            onSave={handleSaveInvItem}
            onClose={() => { setShowInvItemModal(false); setEditingInvItem(null); }}
          />
        )}
      </div>
    </Layout>
  );
}

function InvestmentModal({ players, inventoryItems = [], inventoryCategories = [], inventoryLoaded, onLoadInventory, onSave, onClose, onCreateItem }) {
  const [form, setForm] = useState({
    player_id: '',
    date: new Date().toISOString().split('T')[0],
    type: 'championes',
    description: '',
    amount: '',
    notes: '',
    material_item_id: '',
    material_quantity: 1,
    start_month: currentMonth(),
  });
  const isRecurring = isRecurringType(form.type);
  const selectedPlayer = players.find((p) => p.id === form.player_id);
  
  // Lazy load inventory when modal opens (so the picker has data)
  React.useEffect(() => {
    if (!inventoryLoaded && onLoadInventory) onLoadInventory();
  }, [inventoryLoaded, onLoadInventory]);
  
  
  // Inventory items relevant for current type
  const inventoryRelevant = ['championes', 'indumentaria'].includes(form.type);
  // Match current type → category by name
  const matchingCategoryIds = inventoryCategories
    .filter(c => {
      const n = c.name.toLowerCase();
      if (form.type === 'championes') return n.includes('champion') || n.includes('botin');
      if (form.type === 'indumentaria') return n.includes('indument') || n.includes('ropa') || n.includes('camiset');
      return false;
    })
    .map(c => c.id);
  // Show items with stock>0 belonging to matching category for current type
  const availableInventory = inventoryItems.filter(it => 
    (it.quantity || 0) > 0 && matchingCategoryIds.includes(it.material_category_id)
  );
  const selectedInvItem = inventoryItems.find(it => it.id === form.material_item_id);
  const usingInventory = inventoryRelevant && form.material_item_id;
  const itemHasPrice = (selectedInvItem?.unit_price || 0) > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.player_id) {
      toast.error('Seleccioná un jugador');
      return;
    }
    
    // Viáticos y canastas: pago mensual (se crea un plan; el gasto se registra al marcar cada pago)
    if (isRecurring) {
      if (!form.amount || parseFloat(form.amount) <= 0) {
        toast.error('Ingresá el monto mensual');
        return;
      }
      onSave({
        recurring: true,
        player_id: form.player_id,
        type: form.type,
        amount: parseFloat(form.amount),
        start_month: form.start_month || null,
        notes: form.notes.trim() || null,
      });
      return;
    }

    if (usingInventory) {
      if (!selectedInvItem) {
        toast.error('Item de inventario inválido');
        return;
      }
      const payload = {
        player_id: form.player_id,
        date: form.date,
        type: form.type,
        material_item_id: form.material_item_id,
        material_quantity: parseInt(form.material_quantity) || 1,
        notes: form.notes.trim() || null,
      };
      // If item has no price, user must provide the amount manually
      if (!itemHasPrice) {
        if (!form.amount || parseFloat(form.amount) <= 0) {
          toast.error('Este item no tiene precio cargado. Ingresá el monto.');
          return;
        }
        payload.amount = parseFloat(form.amount);
      }
      onSave(payload);
      return;
    }
    
    // Manual entry
    if (!form.description.trim()) {
      toast.error('Ingresá una descripción');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Ingresá un monto válido');
      return;
    }
    onSave({
      player_id: form.player_id,
      date: form.date,
      type: form.type,
      description: form.description.trim(),
      amount: parseFloat(form.amount),
      notes: form.notes.trim() || null,
    });
  };

  const typeMeta = TYPE_BY_VALUE[form.type];
  const TypeIcon = typeMeta?.icon || MoreHorizontal;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-brand-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 rounded-lg">
              <TypeIcon className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registrar inversión</h2>
              <p className="text-xs text-gray-500">Lo que se le da o paga a un jugador</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-modal-btn">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Player picker — type-ahead combobox */}
          <div>
            <Label className="text-sm font-medium">Jugador *</Label>
            {selectedPlayer ? (
              <div className="mt-1 flex items-center gap-2 p-3 border-2 border-brand-200 bg-brand-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {selectedPlayer.first_name} {selectedPlayer.last_name}
                  </p>
                  {selectedPlayer.total_investment > 0 && (
                    <p className="text-xs text-gray-500">
                      Total invertido hasta hoy: {fmt(selectedPlayer.total_investment)}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setForm({ ...form, player_id: '' })}
                >
                  Cambiar
                </Button>
              </div>
            ) : (
              <div className="mt-1" data-testid="player-search-wrapper">
                <PlayerCombobox
                  value={form.player_id}
                  onChange={(id) => setForm({ ...form, player_id: id })}
                  players={players}
                  placeholder="Buscar jugador por nombre..."
                  dataTestId="player-search-combobox"
                />
              </div>
            )}
          </div>

          {/* Date + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              {isRecurring ? (
                <>
                  <Label className="text-sm font-medium">Primer mes que se paga</Label>
                  <Input
                    type="month"
                    value={form.start_month}
                    onChange={(e) => setForm({ ...form, start_month: e.target.value })}
                    className="mt-1"
                    data-testid="start-month-input"
                  />
                </>
              ) : (
                <>
                  <Label className="text-sm font-medium">Fecha *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="mt-1"
                    required
                  />
                </>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium">Tipo *</Label>
              <Select 
                value={form.type} 
                onValueChange={(v) => {
                  // Clear inventory selection if switching away from championes/indumentaria
                  const stillRelevant = ['championes', 'indumentaria'].includes(v);
                  setForm({ 
                    ...form, 
                    type: v,
                    material_item_id: stillRelevant ? form.material_item_id : '',
                  });
                }}
              >
                <SelectTrigger className="mt-1" data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVESTMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description — becomes a Select with inventory items when type is championes/indumentaria */}
          {inventoryRelevant ? (
            <>
              <div>
                <Label className="text-sm font-medium">Item *</Label>
                <Select
                  value={form.material_item_id || '__manual__'}
                  onValueChange={(v) => {
                    if (v === '__create__') {
                      onCreateItem && onCreateItem(form.type);
                      return;
                    }
                    if (v === '__manual__') {
                      setForm({ ...form, material_item_id: '', material_quantity: 1 });
                    } else {
                      setForm({ ...form, material_item_id: v, material_quantity: 1, description: '', amount: '' });
                    }
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="item-select">
                    <SelectValue placeholder={availableInventory.length === 0 ? 'No hay items en esta categoría' : 'Seleccionar item...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableInventory.map((it) => {
                      const meta = [it.provider, it.size && `T-${it.size}`].filter(Boolean).join(' · ');
                      return (
                        <SelectItem key={it.id} value={it.id}>
                          <span className="flex items-center justify-between gap-3 w-full">
                            <span className="truncate">
                              {it.name}
                              {meta && <span className="text-xs text-gray-500 ml-1">({meta})</span>}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              stock {it.quantity}{(it.unit_price || 0) > 0 ? ` · ${fmt(it.unit_price)}` : ''}
                            </span>
                          </span>
                        </SelectItem>
                      );
                    })}
                    <SelectItem value="__manual__">
                      <span className="text-gray-600 italic">Otro (escribir manualmente)</span>
                    </SelectItem>
                    <SelectItem value="__create__">
                      <span className="text-brand-600 font-medium">+ Crear nuevo item en inventario</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {selectedInvItem && itemHasPrice && (
                  <p className="text-xs text-gray-500 mt-1">
                    Precio unitario: {fmt(selectedInvItem.unit_price)} · stock disponible: {selectedInvItem.quantity}
                  </p>
                )}
              </div>

              {/* Manual description (when "Otro" selected) */}
              {!form.material_item_id && (
                <div>
                  <Label className="text-sm font-medium">Descripción *</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1"
                    placeholder={form.type === 'championes' ? 'Ej: Nike Mercurial talle 42' : 'Ej: Buzo + camiseta entrenamiento'}
                    required
                    data-testid="description-input"
                  />
                </div>
              )}

              {/* Quantity (only when item selected) */}
              {form.material_item_id && (
                <div>
                  <Label className="text-sm font-medium">Cantidad</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedInvItem?.quantity}
                    value={form.material_quantity}
                    onChange={(e) => setForm({ ...form, material_quantity: e.target.value })}
                    className="mt-1 w-32"
                  />
                </div>
              )}

              {/* Amount: shown when manual OR item-without-price */}
              {(!form.material_item_id || !itemHasPrice) && (
                <div>
                  <Label className="text-sm font-medium">
                    Monto *
                    {form.material_item_id && !itemHasPrice && (
                      <span className="text-xs text-amber-600 font-normal ml-2">
                        (item sin precio cargado)
                      </span>
                    )}
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="pl-8 text-lg font-semibold h-12"
                      placeholder="0"
                      required
                      data-testid="amount-input"
                    />
                  </div>
                </div>
              )}

              {/* Auto-calc preview when item with price */}
              {form.material_item_id && itemHasPrice && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-emerald-800">Total a registrar (auto):</span>
                  <span className="text-lg font-bold text-emerald-700">
                    {fmt((selectedInvItem.unit_price || 0) * (parseInt(form.material_quantity) || 1))}
                  </span>
                </div>
              )}
            </>
          ) : isRecurring ? (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="recurring-note">
                Es un pago <strong>mensual</strong>: cada mes aparece solo en "Pagos mensuales" hasta que el jugador quede inactivo.
                El gasto se registra cuando se marca como pagado.
              </div>
              <div>
                <Label className="text-sm font-medium">Monto mensual *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="pl-8 text-lg font-semibold h-12"
                    placeholder="0"
                    required
                    data-testid="amount-input"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Manual description for non-inventory types */}
              <div>
                <Label className="text-sm font-medium">Descripción *</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1"
                  placeholder={
                    form.type === 'championes' ? 'Ej: Nike Mercurial talle 42' :
                      form.type === 'indumentaria' ? 'Ej: Buzo + camiseta de entrenamiento' :
                        form.type === 'estudios_medicos' ? 'Ej: Ecografía rodilla derecha' :
                          form.type === 'psicologo' ? 'Ej: Sesión semanal con Dr Pérez' :
                            form.type === 'educacion' ? 'Ej: Profesora particular de inglés' :
                              form.type === 'viaticos' ? 'Ej: Pasaje + hospedaje viaje a torneo' :
                                form.type === 'canasta_alimentos' ? 'Ej: Canasta mensual abril' :
                                  form.type === 'adelanto' ? 'Ej: Adelanto para gastos personales' :
                                    'Descripción del gasto'
                  }
                  required
                  data-testid="description-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Monto *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="pl-8 text-lg font-semibold h-12"
                    placeholder="0"
                    required
                    data-testid="amount-input"
                  />
                </div>
              </div>
            </>
          )}

          {/* Notes optional */}
          <div>
            <Label className="text-sm font-medium">Notas <span className="text-gray-400">(opcional)</span></Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1"
              placeholder="Aclaración o detalle adicional..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-brand-600 hover:bg-brand-700" data-testid="submit-investment-btn">
              {isRecurring ? 'Configurar pago mensual' : 'Registrar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


function InventoryItemModal({ item, categories, onSave, onClose }) {
  const isEditing = item && item.id;
  const prefilledCatId = item && item._prefilledCategoryId;
  const [form, setForm] = useState({
    name: (isEditing && item.name) || '',
    material_category_id: (isEditing && item.material_category_id) || prefilledCatId || (categories[0]?.id || ''),
    quantity: (isEditing && item.quantity != null) ? item.quantity : 0,
    min_stock: (isEditing && item.min_stock != null) ? item.min_stock : 0,
    unit_price: (isEditing && item.unit_price != null) ? item.unit_price : 0,
    provider: (isEditing && item.provider) || '',
    size: (isEditing && item.size) || '',
    condition: (isEditing && item.condition) || 'bueno',
    notes: (isEditing && item.notes) || '',
  });

  // Detectar si la categoría seleccionada es "championes" → mostrar dropdown de talles
  const selectedCat = categories.find((c) => c.id === form.material_category_id);
  const isChampiones = (selectedCat?.name || '').toLowerCase().includes('champion') ||
                       (selectedCat?.name || '').toLowerCase().includes('botin');

  // Catálogo de talles (shoe_sizes) — sólo se carga si hace falta
  const [shoeSizes, setShoeSizes] = useState([]);
  useEffect(() => {
    if (isChampiones && shoeSizes.length === 0) {
      fetch(`${API_URL}/api/catalogs/shoe_sizes`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => {
          // Ordenar numéricamente para que 5, 5.5, 6, 6.5… aparezca correcto
          const arr = (data || []).slice().sort((a, b) => parseFloat(a) - parseFloat(b));
          setShoeSizes(arr);
        })
        .catch(() => {});
    }
  }, [isChampiones, shoeSizes.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Ingresá un nombre');
    if (!form.material_category_id) return toast.error('Seleccioná categoría');
    onSave({
      name: form.name.trim(),
      material_category_id: form.material_category_id,
      quantity: parseInt(form.quantity) || 0,
      min_stock: parseInt(form.min_stock) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
      provider: form.provider.trim() || null,
      size: form.size.trim() || null,
      condition: form.condition,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Editar item' : 'Agregar al inventario'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-sm font-medium">Nombre *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              placeholder="Ej: Nike Mercurial talle 42"
              required
              data-testid="inv-name-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Categoría *</Label>
            <Select
              value={form.material_category_id}
              onValueChange={(v) => setForm({ ...form, material_category_id: v })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-medium">Stock</Label>
              <Input
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Stock mínimo</Label>
              <Input
                type="number"
                min="0"
                value={form.min_stock}
                onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Precio unitario</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Proveedor</Label>
              <Input
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="mt-1"
                placeholder="Ej: Nike, Adidas, Distribuidor X"
                data-testid="inv-provider-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Talle</Label>
              {isChampiones ? (
                <Select
                  value={form.size || '__none__'}
                  onValueChange={(v) => setForm({ ...form, size: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className="mt-1" data-testid="inv-size-select">
                    <SelectValue placeholder="Seleccionar talle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin talle</SelectItem>
                    {shoeSizes.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="mt-1"
                  placeholder="Ej: M, L, 42"
                  data-testid="inv-size-input"
                />
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-brand-600 hover:bg-brand-700">
              {isEditing ? 'Actualizar' : 'Agregar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
